<script lang="ts">
  import { onMount, afterUpdate, createEventDispatcher } from 'svelte';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import modalHelper from '$lib/helpers/browser/modal-helper';
  import Portal from '../Portal/Portal.svelte';

  const dispatch = createEventDispatcher();

  type $$Props = {
    modalPartialConfig: ModalPartialConfig;
  };

  export let modalPartialConfig: ModalPartialConfig;
  let modalShake: boolean = false;
  const DISMISSIBLE_MODALS: Set<string> = new Set<string>(['standard', 'localeSelector', 'videoPlayer']);

  const isModalDismissible = () => DISMISSIBLE_MODALS.has(modalPartialConfig.type);

  const shakeModal = () => {
    modalShake = true;
    setTimeout(() => {
      modalShake = false;
    }, 300);
  };

  const closeModal = () => {
    // only renable page scrolling if the modal was active
    if (modalPartialConfig.active) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    modalPartialConfig.active = false;
    dispatch('closeModal');
  };

  const handleKeyboardOrMouseEvent = () => {
    if (isModalDismissible()) {
      closeModal();
    } else {
      shakeModal();
    }
  };

  const onClick = () => {
    handleKeyboardOrMouseEvent();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleKeyboardOrMouseEvent();
    }
  };

  let modalWrapper: HTMLElement;

  afterUpdate(() => {
    // disable page scrolling when a modal is opened
    // TODO: move this logic to the BasePage in order to better track page-level modal state
    if (modalPartialConfig.active) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
  });

  onMount(() => {
    const outsideClickHandler = (event: MouseEvent) => {
      modalHelper.outsideClickHandler(event, modalWrapper, handleKeyboardOrMouseEvent);
    };

    document.addEventListener('click', outsideClickHandler, true);

    return () => {
      document.removeEventListener('click', outsideClickHandler, true);
    };
  });
</script>

<svelte:window on:keydown={onKeyDown} />
<!-- need to pass an id here to not break cdp -->
<Portal id={modalPartialConfig.type}>
  <div
    class:country-detection-modal={modalPartialConfig.type === 'countryDetection'}
    class:locale-selector-modal={modalPartialConfig.type === 'localeSelector'}
    class:unsupported-browser-modal={modalPartialConfig.type === 'unsupportedBrowser'}
    class:video-player-modal={modalPartialConfig.type === 'videoPlayer'}
  >
    <div
      class="ModalPartial {modalPartialConfig.customCssClass || ''}"
      {...{ 'data-size': modalPartialConfig.size, 'data-active': modalPartialConfig.active }}
    >
      <div class="modal-scroll">
        <div
          class="modal-wrapper"
          class:modal-shake={modalShake}
          style="background-color: {modalPartialConfig.backgroundColor}"
          bind:this={modalWrapper}
        >
          {#if isModalDismissible()}
            <button
              on:click={onClick}
              class="close-button"
              aria-label={modalPartialConfig.closeButtonText}
              title={modalPartialConfig.closeButtonText}
            />
          {/if}
          <div class:content={modalPartialConfig.type !== 'standard' && modalPartialConfig.type !== 'videoPlayer'}>
            {#if modalPartialConfig.type === 'standard'}
              {#if modalPartialConfig.sections}
                <DynamicChildrenComponent children={modalPartialConfig.sections} />
              {/if}
            {:else}
              <h4 class="modal-title">{modalPartialConfig.title}</h4>
              <slot />
            {/if}
          </div>
        </div>
      </div>
    </div>
  </div>
</Portal>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/variables.scss' as vars;
  @import './ModalPartial--countryDetection.scss';
  @import './ModalPartial--localeSelector.scss';
  @import './ModalPartial--unsupportedBrowser.scss';

  @layer standard-components {
    .ModalPartial {
      position: fixed;
      width: 100%;
      height: 100%;
      top: 50%;
      bottom: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      max-width: none; // new grid fix
      margin: 0 auto;
      opacity: 0;
      visibility: hidden;
      z-index: -1;
      pointer-events: none;
      overflow: hidden;
      transition:
        opacity 0s var(--duration-leave),
        visibility 0s var(--duration-leave),
        z-index 0s var(--duration-leave);
    }

    .ModalPartial::before {
      content: '';
      display: block;
      position: fixed;
      top: 0;
      bottom: 0;
      width: 100%;
      max-width: none; // new grid fix
      margin: 0 auto;
      opacity: 0;
      background-color: black;
      transition: opacity var(--duration-leave) var(--easing-accelerate);
    }

    .ModalPartial[data-active='true'] {
      opacity: 1;
      visibility: visible;
      z-index: 1500;
      pointer-events: auto;
      transition:
        opacity 0s 0s,
        visibility 0s 0s,
        z-index 0s 0s;
    }

    .ModalPartial[data-active='true']::before {
      opacity: 0.5;
      transition: opacity var(--duration-complex) var(--easing-decelerate);
    }

    .modal-scroll {
      z-index: 0;
      display: flex;
      position: fixed;
      top: 0;
      bottom: 0;
      width: 100%;
      max-width: none;
      margin: 0 auto;
      text-align: center;
      overflow: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
      transform: translate3d(-50%, 100%, 0);
      transition:
        opacity var(--duration-leave) var(--easing-accelerate),
        transform var(--duration-leave) var(--easing-accelerate);
      left: 50%;
    }

    .ModalPartial[data-active='true'] .modal-scroll {
      transform: translate3d(-50%, 0, 0);
      transition:
        opacity var(--duration-complex) var(--easing-decelerate),
        transform var(--duration-complex) var(--easing-decelerate);
    }

    .modal-wrapper {
      position: relative;
      width: 100%;
      min-height: 100%;
      max-width: none;
      margin: auto;
      background-color: var(--color-white);
      text-align: left;
    }

    .modal-title {
      margin-bottom: var(--space-20);
    }

    .close-button {
      position: absolute;
      display: block;
      opacity: 0;
      z-index: 1;
      top: var(--space-10);
      right: var(--space-10);
      width: 45px;
      height: 45px;
      border: none;
      background: none;
      cursor: pointer;
    }

    .close-button::before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      border-radius: 5px;
      background-color: var(--color-gray-05);
      opacity: 0.75;
      transition: opacity var(--duration-quick) var(--easing-sharp);
    }

    .close-button::after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTQuNzA3IDMuMjkzYTEgMSAwIDEgMC0xLjQxNCAxLjQxNEwxMC41ODYgMTJsLTcuMjkzIDcuMjkzYTEgMSAwIDEgMCAxLjQxNCAxLjQxNEwxMiAxMy40MTRsNy4yOTMgNy4yOTNhMSAxIDAgMSAwIDEuNDE0LTEuNDE0TDEzLjQxNCAxMmw3LjI5My03LjI5M2ExIDEgMCAxIDAtMS40MTQtMS40MTRMMTIgMTAuNTg2IDQuNzA3IDMuMjkzeiIgZmlsbD0iIzAwMCIvPgo8L3N2Zz4K')
        center center no-repeat;
    }

    .close-button:hover::before {
      opacity: 1;
    }

    .ModalPartial[data-active='true'] .close-button {
      opacity: 1;
      transition: opacity var(--duration-quick) var(--easing-sharp) var(--duration-complex);
    }

    .ModalPartial[data-size='fullscreen'] .modal-wrapper {
      width: 100%;
    }

    @media (min-width: vars.$breakpoint-width-extra-small) {
      .ModalPartial[data-size='extra-small'] .modal-scroll {
        padding: var(--space-80) 0;
      }

      .ModalPartial[data-size='extra-small'] .modal-wrapper {
        display: inline-block;
        width: auto;
        min-height: 0;
        border-radius: 5px;
        padding: var(--space-60);
      }

      .ModalPartial[data-size='extra-small'] .content {
        width: var(--container-width-base);
      }
    }

    @media (min-width: vars.$breakpoint-width-small) {
      .ModalPartial[data-size='small'] .modal-scroll {
        padding: var(--space-80) 0;
      }

      .ModalPartial[data-size='small'] .modal-wrapper {
        display: inline-block;
        width: auto;
        min-height: 0;
        border-radius: 5px;
        padding: var(--space-60);
      }

      .ModalPartial[data-size='small'] .content {
        width: var(--container-width-extra-small);
      }
    }

    @media (min-width: vars.$breakpoint-width-medium) {
      .ModalPartial[data-size='medium'] .modal-scroll {
        padding: var(--space-80) 0;
      }

      .ModalPartial[data-size='medium'] .modal-wrapper {
        display: inline-block;
        width: auto;
        min-height: 0;
        border-radius: 5px;
        padding: var(--space-60);
      }

      .ModalPartial[data-size='medium'] .content {
        width: var(--container-width-small);
      }
    }

    @media (min-width: vars.$breakpoint-width-large) {
      .ModalPartial[data-size='large'] .modal-scroll {
        padding: var(--space-80) 0;
      }

      .ModalPartial[data-size='large'] .modal-wrapper {
        display: inline-block;
        width: auto;
        min-height: 0;
        border-radius: 5px;
        padding: var(--space-60);
      }

      .ModalPartial[data-size='large'] .content {
        width: var(--container-width-medium);
      }
    }

    @media (min-width: vars.$breakpoint-width-extra-large) {
      .ModalPartial[data-size='extra-large'] .modal-scroll {
        padding: var(--space-80) 0;
      }

      .ModalPartial[data-size='extra-large'] .modal-wrapper {
        display: inline-block;
        width: auto;
        min-height: 0;
        border-radius: 5px;
        padding: var(--space-60);
      }

      .ModalPartial[data-size='extra-large'] .content {
        width: var(--container-width-large);
      }
    }

    .modal-shake {
      animation-name: shake-dialog;
      animation-duration: var(--duration-default);
    }

    @keyframes shake-dialog {
      0% {
        transform: translate3d(0, 0, 0);
      }

      20% {
        transform: translate3d(-1%, 0, 0);
      }

      40% {
        transform: translate3d(1%, 0, 0);
      }

      60% {
        transform: translate3d(-1%, 0, 0);
      }

      80% {
        transform: translate3d(1%, 0, 0);
      }

      100% {
        transform: translate3d(0, 0, 0);
      }
    }
  }
</style>
